// Shared helpers for collapsing PCN documents that belong to the same JournalGroup.
//
// These mirror the server-side rule (Accounting.Services PcnGroupMerge): a group is
// merge-eligible only when every document in the group shares the same VAT rate. Within
// those groups, documents are collapsed only when they also share the same reference and
// document date (a duplicate with a different reference/date stays as its own row).
//
// Used by both the PCN report details view and the "check" result accordion so the
// on-screen audit matches exactly what is sent to the tax authority.

const groupIdOf = (doc) => doc?.groupId ?? doc?.GroupId ?? null;

const normalizeReference = (ref) => String(ref ?? '').trim();

/** Normalize to yyyy-mm-dd (date only) so time-of-day / format differences do not block a merge. */
const normalizeDocumentDate = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
        // ISO or yyyy-mm-dd / yyyyMMdd
        const m = val.match(/^(\d{4})-?(\d{2})-?(\d{2})/);
        if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val).trim();
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${day}`;
};

/**
 * Build the merge key: group + VAT + reference + document date.
 * Only documents with an identical key are summed into one row.
 */
export const buildPcnMergeKey = (doc) => {
    const gid = groupIdOf(doc);
    if (!gid) return null;
    const vat = doc.vat ?? doc.VAT ?? 0;
    return `${gid}|${vat}|${normalizeReference(doc.reference ?? doc.Reference)}|${normalizeDocumentDate(doc.documentDate ?? doc.DocumentDate)}`;
};

/**
 * Returns a Set of group ids whose documents (across incomes, expenses and equipment)
 * all share a single VAT rate, and are therefore eligible to be merged into one row.
 * @param {object} pcnReportDetails - object with incomes / expenses / equipmentTransactions arrays.
 */
export const computeUniformVatGroupIds = (pcnReportDetails) => {
    const result = new Set();
    if (!pcnReportDetails) return result;
    const vatByGroup = new Map();
    const collect = (docs) => (docs || []).forEach((d) => {
        const gid = groupIdOf(d);
        if (!gid) return;
        if (!vatByGroup.has(gid)) vatByGroup.set(gid, new Set());
        vatByGroup.get(gid).add(d.vat ?? 0);
    });
    collect(pcnReportDetails.incomes);
    collect(pcnReportDetails.expenses);
    collect(pcnReportDetails.equipmentTransactions);
    vatByGroup.forEach((set, gid) => { if (set.size === 1) result.add(gid); });
    return result;
};

/**
 * Collapses documents that belong to a uniform-VAT group and share the same reference +
 * document date into a single combined row, summing their amounts (and any backend display
 * VAT/net values). The combined row keeps the position of the group's first matching document
 * and exposes:
 *   _grouped        : true
 *   _groupId        : the JournalGroup id (for expand/collapse)
 *   _mergeKey       : full merge key used
 *   _groupedCount   : how many documents were merged
 *   _groupedUploads : the original upload numbers
 *   _children       : the original individual documents (for expand/collapse)
 * Ungrouped documents, mixed-VAT groups, and same-group docs with different ref/date
 * are returned unchanged.
 * @param {Array} docs
 * @param {Set} uniformVatGroupIds - result of computeUniformVatGroupIds.
 */
export const collapseGroupedSameVatDocs = (docs, uniformVatGroupIds) => {
    if (!docs || docs.length === 0) return docs || [];
    const out = [];
    const indexByKey = new Map();
    for (const doc of docs) {
        const gid = groupIdOf(doc);
        if (gid && uniformVatGroupIds && uniformVatGroupIds.has(gid)) {
            const key = buildPcnMergeKey(doc);
            if (key && indexByKey.has(key)) {
                const target = out[indexByKey.get(key)];
                target.amount = (target.amount || 0) + (doc.amount || 0);
                const dVat = doc.displayVatAmount ?? doc.DisplayVatAmount;
                const dNet = doc.displayAmountBeforeVat ?? doc.DisplayAmountBeforeVat;
                if (target._displayVat != null && dVat != null) {
                    target._displayVat += dVat;
                    target._displayNet += dNet;
                    target.displayVatAmount = target._displayVat;
                    target.displayAmountBeforeVat = target._displayNet;
                }
                target._groupedCount += 1;
                target._children.push(doc);
                if (doc.uploadNumber != null) target._groupedUploads.push(doc.uploadNumber);
                continue;
            }
            const rawVat = doc.displayVatAmount ?? doc.DisplayVatAmount;
            const rawNet = doc.displayAmountBeforeVat ?? doc.DisplayAmountBeforeVat;
            const clone = {
                ...doc,
                _grouped: true,
                _groupId: gid,
                _mergeKey: key,
                _groupedCount: 1,
                _children: [doc],
                _groupedUploads: doc.uploadNumber != null ? [doc.uploadNumber] : [],
                _displayVat: rawVat != null ? rawVat : null,
                _displayNet: rawNet != null ? rawNet : null,
            };
            if (key) indexByKey.set(key, out.length);
            out.push(clone);
        } else {
            out.push(doc);
        }
    }
    return out;
};
